import { z } from 'zod';
/**
 * Environment contract shared by API and worker (guide §16).
 * Values are validated at process start. Production refuses insecure defaults.
 * Secrets are never echoed back in error messages.
 */
const PLACEHOLDER_SECRETS = new Set([
    '',
    'replace_with_random_secret',
    'change_me',
    'changeme',
    'secret',
]);
const MIN_PRODUCTION_SECRET_LENGTH = 32;
const booleanFromEnv = z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0', 'yes', 'no'])])
    .transform((v) => v === true || v === 'true' || v === '1' || v === 'yes');
const portSchema = z.coerce.number().int().min(1).max(65535);
const optionalString = z
    .string()
    .optional()
    .transform((v) => (v === undefined || v.trim() === '' ? undefined : v.trim()));
export const nodeEnvSchema = z.enum(['development', 'test', 'production']);
export const aiProviderSchema = z.enum(['mock', 'openai']);
export const logLevelSchema = z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace']);
const baseEnvSchema = z.object({
    NODE_ENV: nodeEnvSchema.default('development'),
    LOG_LEVEL: logLevelSchema.default('info'),
    DATABASE_URL: z.url({ protocol: /^postgres(ql)?$/ }),
    REDIS_URL: z.url({ protocol: /^rediss?$/ }),
    ADMIN_ORIGIN: z.url(),
    API_PUBLIC_URL: z.url(),
    WIDGET_ORIGIN: z.url(),
    ALLOW_LOCAL_HTTP: booleanFromEnv.default(false),
    AI_PROVIDER: aiProviderSchema.default('mock'),
    OPENAI_API_KEY: optionalString,
    OPENAI_CHAT_MODEL: optionalString,
    OPENAI_EMBEDDING_MODEL: z.string().min(1).default('text-embedding-3-small'),
    EMBEDDING_DIMENSIONS: z.coerce.number().int().min(16).max(8192).default(1536),
    JWT_ACCESS_SECRET: z.string(),
    REFRESH_TOKEN_PEPPER: z.string(),
    INSTALLATION_TOKEN_PEPPER: z.string(),
    WIDGET_TOKEN_SECRET: z.string(),
    STORAGE_ROOT: z.string().min(1).default('./data/private'),
    /** Semver floor advertised to WordPress plugins (empty = no upgrade banner). */
    PLUGIN_MIN_VERSION: z.string().max(32).optional().default(''),
    SMTP_HOST: optionalString,
    SMTP_PORT: z
        .union([z.literal(''), portSchema])
        .optional()
        .transform((v) => (v === '' ? undefined : v)),
    SMTP_USER: optionalString,
    SMTP_PASSWORD: optionalString,
    MAIL_FROM: optionalString,
    SEED_ADMIN_EMAIL: optionalString,
    SEED_ADMIN_PASSWORD: optionalString,
});
const SECRET_KEYS = [
    'JWT_ACCESS_SECRET',
    'REFRESH_TOKEN_PEPPER',
    'INSTALLATION_TOKEN_PEPPER',
    'WIDGET_TOKEN_SECRET',
];
const ORIGIN_KEYS = ['ADMIN_ORIGIN', 'API_PUBLIC_URL', 'WIDGET_ORIGIN'];
function isLocalOrigin(url) {
    try {
        const { hostname } = new URL(url);
        return (hostname === 'localhost' ||
            hostname === '127.0.0.1' ||
            hostname === '::1' ||
            hostname.endsWith('.localhost'));
    }
    catch {
        return false;
    }
}
/**
 * Cross-field rules. Applied to the base schema so API/worker share the same guarantees.
 */
function applyEnvRules(env, ctx) {
    const isProduction = env.NODE_ENV === 'production';
    for (const key of SECRET_KEYS) {
        const value = env[key];
        if (PLACEHOLDER_SECRETS.has(value.trim().toLowerCase())) {
            ctx.addIssue({
                code: 'custom',
                path: [key],
                message: 'must be set to a random secret (placeholder value detected)',
            });
        }
        else if (isProduction && value.length < MIN_PRODUCTION_SECRET_LENGTH) {
            ctx.addIssue({
                code: 'custom',
                path: [key],
                message: `must be at least ${MIN_PRODUCTION_SECRET_LENGTH} characters in production`,
            });
        }
    }
    for (const key of ORIGIN_KEYS) {
        const value = env[key];
        const local = isLocalOrigin(value);
        const insecure = value.startsWith('http://');
        if ((local || insecure) && !env.ALLOW_LOCAL_HTTP) {
            ctx.addIssue({
                code: 'custom',
                path: [key],
                message: 'localhost/http origins require ALLOW_LOCAL_HTTP=true (never in production)',
            });
        }
    }
    if (isProduction) {
        if (env.ALLOW_LOCAL_HTTP) {
            ctx.addIssue({
                code: 'custom',
                path: ['ALLOW_LOCAL_HTTP'],
                message: 'must be false in production',
            });
        }
        if (env.AI_PROVIDER === 'mock') {
            ctx.addIssue({
                code: 'custom',
                path: ['AI_PROVIDER'],
                message: 'mock provider is not allowed in production',
            });
        }
        if (/:(change_me|password|postgres)@/i.test(env.DATABASE_URL)) {
            ctx.addIssue({
                code: 'custom',
                path: ['DATABASE_URL'],
                message: 'placeholder database password is not allowed in production',
            });
        }
        const widgetOrigin = new URL(env.WIDGET_ORIGIN).origin;
        if (widgetOrigin === new URL(env.ADMIN_ORIGIN).origin ||
            widgetOrigin === new URL(env.API_PUBLIC_URL).origin) {
            ctx.addIssue({
                code: 'custom',
                path: ['WIDGET_ORIGIN'],
                message: 'must be a separate origin from the admin and the API in production',
            });
        }
    }
    if (env.AI_PROVIDER === 'openai') {
        if (!env.OPENAI_API_KEY) {
            ctx.addIssue({
                code: 'custom',
                path: ['OPENAI_API_KEY'],
                message: 'required when AI_PROVIDER=openai',
            });
        }
        if (!env.OPENAI_CHAT_MODEL) {
            ctx.addIssue({
                code: 'custom',
                path: ['OPENAI_CHAT_MODEL'],
                message: 'required when AI_PROVIDER=openai (choose a verified model, see docs/DECISIONS.md D-0010)',
            });
        }
    }
}
export const apiEnvSchema = baseEnvSchema
    .extend({
    API_PORT: portSchema.default(5000),
})
    .superRefine(applyEnvRules);
export const workerEnvSchema = baseEnvSchema
    .extend({
    WORKER_PORT: portSchema.default(5001),
})
    .superRefine(applyEnvRules);
export class EnvValidationError extends Error {
    issues;
    constructor(issues) {
        const lines = issues.map((i) => `  - ${i.path}: ${i.message}`).join('\n');
        super(`Invalid environment configuration:\n${lines}`);
        this.name = 'EnvValidationError';
        this.issues = issues;
    }
}
function formatIssues(error) {
    // Only key names and rule messages are reported; raw values (possibly secrets) are never included.
    return error.issues.map((issue) => ({
        path: issue.path.map(String).join('.') || '(root)',
        message: issue.message,
    }));
}
export function parseApiEnv(source = process.env) {
    const result = apiEnvSchema.safeParse(source);
    if (!result.success)
        throw new EnvValidationError(formatIssues(result.error));
    return result.data;
}
export function parseWorkerEnv(source = process.env) {
    const result = workerEnvSchema.safeParse(source);
    if (!result.success)
        throw new EnvValidationError(formatIssues(result.error));
    return result.data;
}
//# sourceMappingURL=env.js.map